/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business 汎用ドロップダウン Picker。popover 型の単一選択。enum / preset 値の選択
 *  (scope / 期間 / 比較期間 など) で使う。renderOption / renderValue で候補行と
 *  トリガーの見た目 (色付きラベル等) を差し替えられ、triggerVariant="bare" で
 *  トリガーの枠線・背景・キャレットを外せる。複数選択が必要な場合は MultiPicker を使うこと
 * @graph-connects none
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useFloatingPosition } from '../../../hooks/useFloatingPosition';
import { cn } from '../../../lib/cn';

export interface PickerOption {
  /** 識別子 (onChange に返される) */
  value: string;
  /** 表示ラベル */
  label: string;
  /** 補助説明 (行の 2 行目に出る) */
  description?: string;
  /** 選択不能 */
  disabled?: boolean;
}

export interface PickerProps {
  /** 候補一覧 */
  options: readonly PickerOption[];
  /** 選択中の value (未選択は null) */
  value: string | null;
  /** 選択ハンドラ */
  onChange: (value: string | null) => void;
  /** 未選択時の placeholder */
  placeholder?: string;
  /** ラベル (上に小さく表示) */
  label?: string;
  /** 検索フォームを表示する (default: false。enum 用なので普段は不要) */
  searchable?: boolean;
  /** 検索フォームの placeholder */
  searchPlaceholder?: string;
  /** 検索ヒット 0 件時の文言 */
  emptyMessage?: string;
  /** 全体を無効化 */
  disabled?: boolean;
  /** トリガー要素の id (label の for と紐づけ) */
  id?: string;
  /** ARIA ラベル (label prop が無い時に必須) */
  'aria-label'?: string;
  /** 追加 className (ルート要素) */
  className?: string;
  /**
   * 候補行のラベル部分の描画を差し替える (未指定なら `option.label` をテキスト表示)。
   * 状態を色付きチップ (ラベル風) で見せたい場合などに使う。`description` の行や
   * 選択チェックマークは差し替え対象外で従来どおり描画される。
   */
  renderOption?: (option: PickerOption) => ReactNode;
  /**
   * トリガー (選択中の値) の描画を差し替える (未指定なら `option.label` をテキスト表示)。
   * `renderOption` と揃えて、閉じている時も同じ見た目にするために使う。
   */
  renderValue?: (option: PickerOption) => ReactNode;
  /**
   * トリガーの見た目。
   * - `default`: 枠線・背景・キャレット付きの通常のセレクト外観
   * - `bare`   : 枠線・背景・パディング・キャレットを外し、`renderValue` が描いたものだけを見せる。
   *   値そのもの (色付きラベル等) をクリック領域にしたい時に使う。フォーカスリングは
   *   残す (キーボード操作でどこに居るか分からなくなるため)。
   */
  triggerVariant?: 'default' | 'bare';
}

/**
 * 汎用 Picker。
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business enum/preset 用の単一選択ドロップダウン
 * @graph-connects none
 */
export function Picker({
  options,
  value,
  onChange,
  placeholder = '選択してください',
  label,
  searchable = false,
  searchPlaceholder = '検索...',
  emptyMessage = '候補がありません',
  disabled = false,
  id,
  'aria-label': ariaLabel,
  className,
  renderOption,
  renderValue,
  triggerVariant = 'default',
}: PickerProps): ReactNode {
  const autoId = useId();
  const triggerId = id ?? `cortex-pk-${autoId}`;
  const listboxId = `${triggerId}-listbox`;
  const labelId = label ? `${triggerId}-label` : undefined;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const popoverRect = useFloatingPosition(triggerRef, open, popoverRef);

  const filtered = useMemo(() => {
    if (!searchable) return [...options];
    const trimmed = query.trim().toLowerCase();
    if (trimmed === '') return [...options];
    return options.filter((o) =>
      [o.label, o.description ?? '', o.value].join(' ').toLowerCase().includes(trimmed),
    );
  }, [options, query, searchable]);

  // open 切り替え時に active index を整える
  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
      return;
    }
    const initial = filtered.findIndex((o) => o.value === value && !o.disabled);
    const firstEnabled = filtered.findIndex((o) => !o.disabled);
    setActiveIndex(initial >= 0 ? initial : firstEnabled);
  }, [open, filtered, value]);

  // 外側クリックで閉じる (popover は portal なので popoverRef も判定に含める)
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !searchable) return;
    searchRef.current?.focus();
  }, [open, searchable]);

  const selectIndex = useCallback(
    (idx: number) => {
      const opt = filtered[idx];
      if (!opt || opt.disabled) return;
      onChange(opt.value);
      setOpen(false);
      setQuery('');
      triggerRef.current?.focus();
    },
    [filtered, onChange],
  );

  const onTriggerKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onPopoverKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let next = activeIndex;
      for (let i = 1; i <= filtered.length; i += 1) {
        const candidate = (activeIndex + i) % filtered.length;
        if (!filtered[candidate]?.disabled) {
          next = candidate;
          break;
        }
      }
      setActiveIndex(next);
      optionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      let next = activeIndex;
      for (let i = 1; i <= filtered.length; i += 1) {
        const candidate = (activeIndex - i + filtered.length) % filtered.length;
        if (!filtered[candidate]?.disabled) {
          next = candidate;
          break;
        }
      }
      setActiveIndex(next);
      optionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      selectIndex(activeIndex);
    }
  };

  const selected = options.find((o) => o.value === value);

  const popover = open ? (
    <div
      ref={popoverRef}
      className={cn('cortex-mp-popover', triggerVariant === 'bare' && 'cortex-mp-popover--bare')}
      role="presentation"
      style={{
        position: 'fixed',
        top: popoverRect?.top ?? 0,
        left: popoverRect?.left ?? 0,
        // bare は trigger が中身ぴったりまで縮むため、trigger 幅に合わせると候補一覧が
        // 潰れる。中身なりの幅にして最小幅だけ CSS で担保する。
        width: triggerVariant === 'bare' ? 'max-content' : (popoverRect?.width ?? 'auto'),
        // 上下どちらにも収まらない時は内部スクロールに切り替える（画面外へ出さない）
        maxHeight: popoverRect ? `${String(popoverRect.maxHeight)}px` : undefined,
        visibility: popoverRect ? 'visible' : 'hidden',
      }}
      onKeyDown={onPopoverKey}
    >
      {searchable ? (
        <div className="cortex-mp-search">
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            placeholder={searchPlaceholder}
            className="cortex-mp-search-input"
            aria-controls={listboxId}
          />
        </div>
      ) : null}
      {filtered.length === 0 ? (
        <p className="cortex-mp-empty">{emptyMessage}</p>
      ) : (
        <ul id={listboxId} role="listbox" className="cortex-mp-listbox">
          {filtered.map((opt, idx) => {
            const isSelected = opt.value === value;
            const isActive = idx === activeIndex;
            return (
              <li
                key={opt.value}
                ref={(el) => {
                  optionRefs.current[idx] = el;
                }}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled}
                data-active={isActive}
                data-selected={isSelected}
                className="cortex-mp-option"
                onMouseEnter={() => {
                  setActiveIndex(idx);
                }}
                onClick={() => {
                  selectIndex(idx);
                }}
              >
                <span className="cortex-mp-option-main">
                  {renderOption ? (
                    renderOption(opt)
                  ) : (
                    <span className="cortex-mp-option-name">{opt.label}</span>
                  )}
                  {opt.description !== undefined && opt.description !== '' ? (
                    <span className="cortex-mp-option-description">{opt.description}</span>
                  ) : null}
                </span>
                {isSelected ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                    style={{ flexShrink: 0, color: 'var(--color-accent-primary)' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={cn('cortex-mp-root', className)}>
      {label ? (
        <label id={labelId} htmlFor={triggerId} className="cortex-mp-label">
          {label}
        </label>
      ) : null}
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        className={cn('cortex-mp-trigger', triggerVariant === 'bare' && 'cortex-mp-trigger--bare')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-labelledby={labelId}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        data-open={open}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        onKeyDown={onTriggerKey}
      >
        <span className="cortex-mp-trigger-content">
          {selected ? (
            <span className="cortex-mp-single-value">
              {renderValue ? (
                renderValue(selected)
              ) : (
                <span className="cortex-mp-single-value-name">{selected.label}</span>
              )}
            </span>
          ) : (
            <span className="cortex-mp-placeholder">{placeholder}</span>
          )}
        </span>
        <svg
          className="cortex-mp-caret"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {popover ? createPortal(popover, document.body) : null}
    </div>
  );
}
